/// <reference types="vite/client" />
import { useEffect } from "react";

import { vlaLockApi } from "../../../api/client";
import { toTitleCase } from "../../../lib/format";
import { isHttpError } from "../../../lib/query/errors";
import { useApprovalStore } from "../../../stores/approvalStore";
import { releaseHeldVlaLock } from "./releaseHeldVlaLock";

/**
 * Acquires the VLA In-Use (deal) lock when a deal is opened and releases it on
 * exit (OR-20985). The acquire result is authoritative: 200 means we hold the
 * lock (editable); 409 means another analyst holds it (read-only + banner). Any
 * other failure fails safe to read-only. There is no polling — the entry-time
 * decision persists until the deal is exited (spec FR-7: no client heartbeat).
 * @param {string} approvalId - The approval/VLA id of the open deal.
 */
export function useVlaLock(approvalId: string): void {
  const startAcquiring = useApprovalStore((state) => state.startAcquiring);
  const setHeldByMe = useApprovalStore((state) => state.setHeldByMe);
  const setHeldByOther = useApprovalStore((state) => state.setHeldByOther);
  const setError = useApprovalStore((state) => state.setError);
  const reset = useApprovalStore((state) => state.reset);

  useEffect(() => {
    if (!approvalId) {
      return;
    }

    let cancelled = false;
    startAcquiring(approvalId);

    void (async () => {
      try {
        await vlaLockApi.acquireLock(approvalId);
        if (cancelled) {
          // The deal changed (or unmounted) before we learned we hold the
          // lock — by now the store's approvalId has moved on, so
          // releaseHeldVlaLock's phase guard can't see this one. Release it
          // directly by id so it isn't orphaned server-side until the
          // backend reaper (FR-7) times it out.
          try {
            await vlaLockApi.releaseLock(approvalId);
          } catch {
            // best-effort — same fail-open rationale as releaseHeldVlaLock
          }
          return;
        }
        setHeldByMe();
      } catch (error) {
        if (cancelled) {
          return;
        }
        if (isHttpError(error, 409)) {
          // Someone else holds it — read the status to surface the holder.
          try {
            const { data } = await vlaLockApi.getLockStatus(approvalId);
            if (cancelled) {
              return;
            }
            // fullName is the populated display name; lockedByUserName is
            // reserved for future recap-service support and always null today.
            const rawHolderName = data.fullName ?? data.lockedByUserName ?? undefined;
            setHeldByOther({
              holderId: data.lockedByUserId,
              holderName: rawHolderName ? toTitleCase(rawHolderName) : undefined,
            });
          } catch {
            if (!cancelled) {
              setHeldByOther({});
            }
          }
        } else {
          setError();
        }
      }
    })();

    return () => {
      cancelled = true;
      // Release only if we actually hold it — `releaseHeldVlaLock` guards on the
      // shared `held-by-me` phase, which our own acquire is the only thing that
      // sets. That store-phase guard is also StrictMode-safe: the phantom first
      // cleanup runs while the phase is still `acquiring` (the PUT hasn't
      // resolved), so it correctly no-ops instead of releasing the live lock.
      void releaseHeldVlaLock();
      // Clear the store for the next deal even when we didn't hold the lock
      // (held-by-other / error / still acquiring) — releaseHeldVlaLock only
      // neutralizes the held-by-me case.
      reset();
    };
  }, [approvalId, startAcquiring, setHeldByMe, setHeldByOther, setError, reset]);

  // Best-effort release if the tab is closed/refreshed before React unmounts.
  // `keepalive` lets the DELETE outlive the page; navigator.sendBeacon can't be
  // used because it only issues POST. This path intentionally bypasses the typed
  // `vlaLockApi`/`wrapBFFClientWithAuth` layer because the generated client has
  // no keepalive-capable method — so it skips the 401 interceptor (fine here:
  // there's no UI left to redirect on unload). If the BFF ever adds a CSRF header
  // to state-changing verbs, this raw fetch must be updated to send it too.
  useEffect(() => {
    /**
     * Fires a keepalive DELETE for the held lock as the page unloads.
     * @returns {void}
     */
    function releaseOnUnload(): void {
      // Read the live store rather than a hook-local flag so this shares the
      // single held-by-me guard with releaseHeldVlaLock and the unmount cleanup.
      const { approvalId: heldId, lock } = useApprovalStore.getState();
      if (lock.phase !== "held-by-me" || !heldId) {
        return;
      }
      // Mirrors the generated VLALockApi.releaseLock path (see client.ts). Kept
      // as a literal only because the generated client isn't keepalive-capable;
      // keep it in sync if the generated `/vlas-in-use` path ever changes.
      const base = import.meta.env.VITE_STEP6_BFF_BASE_URL;
      void (async () => {
        try {
          await fetch(`${base}/api/v1/workbench/applications/${encodeURIComponent(heldId)}/vlas-in-use`, {
            method: "DELETE",
            credentials: "include",
            keepalive: true,
          });
        } catch {
          // ignore — best-effort on unload
        }
      })();
    }
    globalThis.addEventListener("beforeunload", releaseOnUnload);
    return () => globalThis.removeEventListener("beforeunload", releaseOnUnload);
  }, []);
}
